import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import dropZoneImage from "assets/images/drop_zone_image.png";
import {
  ATTACHMENT_FILE_SIZE_LIMIT,
  ATTACHMENT_FILE_SIZE_LIMIT_IN_MB,
} from "config/constant";

interface FileDropZoneProps {
  onChangeFile: (files: File[]) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

const FileDropZone = ({ onChangeFile, containerRef }: FileDropZoneProps) => {
  const [showDropZone, setShowDropZone] = useState(false);
  const containerElement = containerRef?.current;
  const isContained = !!containerElement;

  const { getRootProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      onChangeFile(acceptedFiles);
      setShowDropZone(false);
    },
    multiple: true,
    maxSize: ATTACHMENT_FILE_SIZE_LIMIT,
  });

  const handleMouseLeave = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowDropZone(false);
  };

  useEffect(() => {
    const handleGlobalDragOver = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setShowDropZone(true);
    };

    const handleGlobalDrop = (event: DragEvent) => {
      event.preventDefault();
    };

    window.addEventListener("dragover", handleGlobalDragOver);
    window.addEventListener("drop", handleGlobalDrop);
    window.document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("dragover", handleGlobalDragOver);
      window.removeEventListener("drop", handleGlobalDrop);
      window.document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  const dropZoneElement = showDropZone ? (
    <OverlayStyle $isContained={isContained}>
      <DropZoneStyle {...getRootProps()} $isContained={isContained}>
        <ImageStyle $isContained={isContained} src={dropZoneImage} />
        <TitleStyle $isContained={isContained}>
          파일을 여기에 놓으세요.
        </TitleStyle>
        <DescriptionStyle>
          여러개 가능, 최대 {ATTACHMENT_FILE_SIZE_LIMIT_IN_MB}MB 이하
        </DescriptionStyle>
      </DropZoneStyle>
    </OverlayStyle>
  ) : null;

  if (isContained && containerElement) {
    return createPortal(dropZoneElement, containerElement);
  }

  return dropZoneElement;
};

export default FileDropZone;

const OverlayStyle = styled.div<{ $isContained: boolean }>`
  position: ${({ $isContained }) => ($isContained ? "absolute" : "fixed")};
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  padding: ${({ $isContained }) => ($isContained ? "1.6rem" : "0")};
  box-sizing: border-box;
  border-radius: inherit;

  display: flex;
  justify-content: center;
  align-items: center;

  z-index: ${({ theme, $isContained }) =>
    $isContained ? 1 : theme.zIndex.modal};
`;

const DropZoneStyle = styled.div<{ $isContained: boolean }>`
  width: ${({ $isContained }) => ($isContained ? "100%" : "75rem")};
  height: ${({ $isContained }) => ($isContained ? "100%" : "36rem")};
  max-width: 75rem;
  max-height: 36rem;
  border-radius: 2rem;
  border: 0.1rem dashed ${({ theme }) => theme.color.stroke.white};
  background: ${({ theme }) => theme.color.background.dimedWhite};
  padding: ${({ $isContained }) => ($isContained ? "2.4rem 2rem 2rem" : "0")};
  box-sizing: border-box;
  overflow: ${({ $isContained }) => ($isContained ? "hidden" : "visible")};

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.4rem;
  position: relative;
`;

const ImageStyle = styled.img<{ $isContained: boolean }>`
  width: 11rem;
  height: 20.8rem;
  position: absolute;
  top: ${({ $isContained }) => ($isContained ? "1rem" : "-7rem")};
`;

const TitleStyle = styled.p<{ $isContained: boolean }>`
  margin-top: ${({ $isContained }) => ($isContained ? "14rem" : "17.3rem")};
  color: ${({ theme }) => theme.color.text.white};
  ${({ theme }) => theme.typography.h1.bold}
`;

const DescriptionStyle = styled.p`
  color: ${({ theme }) => theme.color.text.white};
  ${({ theme }) => theme.typography.body1.regular}
`;
